import { useQuery } from '@tanstack/react-query';
import {
  ClockCounterClockwise,
  UsersThree,
  CurrencyGbp,
  Warning,
  Receipt,
} from '@phosphor-icons/react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { fetchMatterIntelligence } from '@/lib/mattersApi';

interface Props {
  matterId: string;
}

// ─── Event label map ──────────────────────────────────────────

const EVENT_LABELS: Record<string, string> = {
  matter_created:         'Matter opened',
  hearing_scheduled:      'Hearing scheduled',
  hearing_updated:        'Hearing updated',
  hearing_outcome:        'Outcome recorded',
  hearing_adjourned:      'Hearing adjourned',
  hearing_cancelled:      'Hearing cancelled',
  document_uploaded:      'Document uploaded',
  note_added:             'Note added',
  fee_note_issued:        'Fee note issued',
  fee_note_reminder_sent: 'Payment reminder sent',
  fee_note_paid:          'Payment received',
  fee_note_written_off:   'Fee note written off',
  task_assigned:          'Task assigned',
  task_completed:         'Task completed',
  allocation_accepted:    'Brief accepted',
  allocation_declined:    'Brief declined',
  conference_scheduled:   'Conference scheduled',
  conference_completed:   'Conference completed',
  status_changed:         'Status changed',
};

function labelForEvent(type: string): string {
  return EVENT_LABELS[type] ?? type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days = Math.floor(diff / 86_400_000);
  const weeks = Math.floor(days / 7);
  if (diff < 60_000)    return 'just now';
  if (hours < 1)        return `${mins}m ago`;
  if (days < 1)         return `${hours}h ago`;
  if (weeks < 1)        return `${days}d ago`;
  if (weeks < 5)        return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function formatGbp(amount: number): string {
  if (amount >= 1_000_000) return `£${(amount / 1_000_000).toFixed(1)}m`;
  if (amount >= 1_000)     return `£${(amount / 1_000).toFixed(0)}k`;
  return `£${amount.toFixed(0)}`;
}

// ─── Component ───────────────────────────────────────────────

export function MatterIntelligencePanel({ matterId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['matter-intelligence', matterId],
    queryFn: () => fetchMatterIntelligence(matterId),
    staleTime: 60_000,
    enabled: !!matterId,
  });

  if (isLoading || !data) return null;

  const { lastEvents, taskSummary, participants, feeSummary, dormancy, feeCapture } = data;

  const hasFees = feeSummary.wip > 0 || feeSummary.outstanding > 0 || feeSummary.collected > 0;

  const teamEntries = [
    participants.leadBarrister && { label: 'Lead', value: participants.leadBarrister },
    participants.juniorBarrister && { label: 'Junior', value: participants.juniorBarrister },
    participants.clerk && { label: 'Clerk', value: participants.clerk },
    participants.instructingSolicitor && {
      label: 'Solicitor',
      value: participants.instructingSolicitor.firm
        ?? participants.instructingSolicitor.name
        ?? '—',
    },
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return (
    <Card>
      <CardHeader className="pb-2 pt-4">
        <CardTitle className="text-sm font-semibold text-foreground">Operational brief</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pb-4">

        {/* ── Dormant matter warning ── */}
        {dormancy.isDormant && (
          <div className="flex items-start gap-2 rounded border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 px-3 py-2">
            <Warning className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />
            <span className="text-xs text-amber-800 dark:text-amber-300">
              No activity for {dormancy.daysSinceActivity} days
            </span>
          </div>
        )}

        {/* ── Fee capture prompt ── */}
        {feeCapture.completedHearingsWithoutFeeNote > 0 && (
          <div className="flex items-start gap-2 rounded border border-blue-200 dark:border-blue-500/30 bg-blue-50 dark:bg-blue-500/10 px-3 py-2">
            <Receipt className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600 dark:text-blue-400" weight="fill" />
            <span className="text-xs text-blue-800 dark:text-blue-300">
              {feeCapture.completedHearingsWithoutFeeNote === 1
                ? '1 completed hearing — no fee note raised'
                : `${feeCapture.completedHearingsWithoutFeeNote} completed hearings — no fee note raised`}
            </span>
          </div>
        )}

        {/* ── Recent activity ── */}
        {lastEvents.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-1.5">
              <ClockCounterClockwise className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                Activity
              </span>
            </div>
            <div className="space-y-1.5">
              {lastEvents.map((e) => (
                <div key={e.id} className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs text-foreground/80">
                    {labelForEvent(e.eventType)}
                  </span>
                  <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/60">
                    {relativeTime(e.occurredAt)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Fees at a glance ── */}
        {hasFees && (
          <div>
            <div className="mb-2 flex items-center gap-1.5">
              <CurrencyGbp className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                Finances
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {feeSummary.wip > 0 && (
                <>
                  <span className="text-[10px] text-muted-foreground">WIP</span>
                  <span className="text-right text-xs font-medium tabular-nums text-foreground">
                    {formatGbp(feeSummary.wip)}
                  </span>
                </>
              )}
              {feeSummary.outstanding > 0 && (
                <>
                  <span className="text-[10px] text-muted-foreground">Outstanding</span>
                  <span className="text-right text-xs font-medium tabular-nums text-amber-700 dark:text-amber-300">
                    {formatGbp(feeSummary.outstanding)}
                  </span>
                </>
              )}
              {feeSummary.collected > 0 && (
                <>
                  <span className="text-[10px] text-muted-foreground">Collected</span>
                  <span className="text-right text-xs font-medium tabular-nums text-emerald-700 dark:text-emerald-300">
                    {formatGbp(feeSummary.collected)}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Tasks status line ── */}
        {taskSummary.total > 0 && (
          <div className="flex items-center justify-between border-t border-border/50 pt-3">
            <span className="text-xs text-muted-foreground">
              {taskSummary.pending > 0
                ? `${taskSummary.pending} task${taskSummary.pending !== 1 ? 's' : ''} pending`
                : `All ${taskSummary.completed} task${taskSummary.completed !== 1 ? 's' : ''} complete`}
            </span>
            {taskSummary.overdue > 0 && (
              <span className="text-[10px] font-semibold text-red-600 dark:text-red-400">
                {taskSummary.overdue} overdue
              </span>
            )}
          </div>
        )}

        {/* ── Team ── */}
        {teamEntries.length > 0 && (
          <div>
            <div className="mb-2 flex items-center gap-1.5">
              <UsersThree className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                Team
              </span>
            </div>
            <div className="space-y-1.5">
              {teamEntries.map((entry) => (
                <div key={entry.label} className="flex items-baseline justify-between gap-2">
                  <span className="shrink-0 text-[10px] text-muted-foreground/60 w-14">
                    {entry.label}
                  </span>
                  <span className="truncate text-right text-xs text-foreground/80">
                    {entry.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
